import type { OrderEventHistoryItem } from '@/api/hooks/useOrderHistory'
import { formatTimestamp } from '@/utils/dateFormatters'
import { ArrowRightOutlined } from '@ant-design/icons'
import { Horizontal, Texto, Vertical } from '@gravitate-js/excalibrr'
import { Card, theme } from 'antd'
import {
  Ban,
  CheckCircle2,
  CircleDot,
  FilePlus2,
  Inbox,
  XCircle,
} from 'lucide-react'
import type React from 'react'

interface OrderEventCardProps {
  event: OrderEventHistoryItem
}

function getEventPresentation(
  title: string,
  logStatus: string,
  token: {
    colorSuccess: string
    colorError: string
    colorInfo: string
    colorWarning: string
  },
): { color: string; icon: React.ReactNode } {
  const iconSize = 14
  if (title === 'Order Request Rejected' || logStatus === 'failed') {
    return {
      color: token.colorError,
      icon: <XCircle size={iconSize} />,
    }
  }
  if (title === 'Cancellation Applied to Destination') {
    return {
      color: token.colorError,
      icon: <Ban size={iconSize} />,
    }
  }
  if (
    title === 'Cancellation Received' ||
    title === 'Cancellation Received and Skipped'
  ) {
    return {
      color: token.colorWarning,
      icon: <Ban size={iconSize} />,
    }
  }
  if (title === 'Order Request Accepted' || title === 'Order Created') {
    return {
      color: token.colorSuccess,
      icon: <CheckCircle2 size={iconSize} />,
    }
  }
  if (title === 'Order Request Created') {
    return {
      color: token.colorInfo,
      icon: <FilePlus2 size={iconSize} />,
    }
  }
  if (title === 'Order Received') {
    return {
      color: token.colorInfo,
      icon: <Inbox size={iconSize} />,
    }
  }
  return {
    color: token.colorInfo,
    icon: <CircleDot size={iconSize} />,
  }
}

export function OrderEventCard({ event }: OrderEventCardProps) {
  const { token } = theme.useToken()
  const { color, icon } = getEventPresentation(
    event.title,
    event.log_status,
    token,
  )
  const isHighlighted =
    event.title === 'Order Request Rejected' ||
    event.title === 'Cancellation Applied to Destination'

  return (
    <Horizontal gap={12} style={{ position: 'relative' }}>
      {/* Timeline dot */}
      <div
        style={{
          width: 12,
          height: 12,
          borderRadius: '50%',
          backgroundColor: color,
          flexShrink: 0,
          marginTop: 18,
          zIndex: 1,
        }}
      />

      <Card
        size='small'
        style={{
          flex: 1,
          borderLeft: isHighlighted ? `3px solid ${color}` : undefined,
          backgroundColor: isHighlighted
            ? `color-mix(in srgb, ${color} 5%, transparent)`
            : undefined,
        }}
        styles={{ body: { padding: 12 } }}
      >
        <Vertical gap={8}>
          {/* Row 1: Icon + Title + Timestamp */}
          <Horizontal justifyContent='space-between' alignItems='center'>
            <Horizontal gap={8} alignItems='center'>
              <span style={{ color, display: 'flex' }}>{icon}</span>
              <Texto weight='600'>{event.title}</Texto>
            </Horizontal>
            <Texto appearance='hint' style={{ fontSize: 12 }}>
              {formatTimestamp(event.created_on)}
            </Texto>
          </Horizontal>

          {/* Row 2: Direction */}
          <Horizontal gap={6} alignItems='center'>
            <Texto weight='600' style={{ fontSize: 13 }}>
              {event.origin_tenant_name}
            </Texto>
            <ArrowRightOutlined
              style={{ fontSize: 10, color: token.colorTextTertiary }}
            />
            <Texto weight='600' style={{ fontSize: 13 }}>
              {event.destination_tenant_name ?? 'N/A'}
            </Texto>
          </Horizontal>

          {/* Row 3: Message */}
          <Texto style={{ fontSize: 12 }}>{event.message}</Texto>
        </Vertical>
      </Card>
    </Horizontal>
  )
}
